import React, { useState, useMemo } from 'react';
import ShowcasePageWrapper from '../../showcase/shared/ShowcasePageWrapper';
import {
  SLDSLineChart,
  SLDSDoughnutChart,
  SLDSBarChart,
  MiniDonut,
  SLDS_CHART_COLORS,
  SLDS_CHART_COLORS_ALPHA,
} from '../../showcase/shared/ChartComponents';
import Button from '@salesforce/design-system-react/components/button';
import Icon from '@salesforce/design-system-react/components/icon';
import DataTable from '@salesforce/design-system-react/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/components/data-table/cell';
import Tabs from '@salesforce/design-system-react/components/tabs';
import TabsPanel from '@salesforce/design-system-react/components/tabs/panel';
import ExpandableSection from '@salesforce/design-system-react/components/expandable-section';

// ---------------------------------------------------------------------------
// Style constants
// ---------------------------------------------------------------------------
const COLORS = {
  brand: '#0176d3',
  success: '#2e844a',
  warning: '#fe9339',
  error: '#c23934',
  border: '#dddbda',
  bg: '#f3f3f3',
  textPrimary: '#080707',
  textSecondary: '#706e6b',
  white: '#ffffff',
  purple: '#9050e9',
  teal: '#06a59a',
};

const cardStyle = {
  backgroundColor: COLORS.white,
  border: `1px solid ${COLORS.border}`,
  borderRadius: '4px',
  padding: '20px',
  marginBottom: '16px',
};

const fieldLabelStyle = {
  fontSize: '12px',
  color: COLORS.textSecondary,
  fontWeight: '400',
  marginBottom: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const fieldValueStyle = {
  fontSize: '14px',
  color: COLORS.textPrimary,
  fontWeight: '400',
  marginBottom: '16px',
};

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------
const Badge = ({ label, color, bgColor }) => (
  <span style={{
    display: 'inline-block', backgroundColor: bgColor, color: color,
    padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', lineHeight: '20px',
  }}>
    {label}
  </span>
);

const FieldDisplay = ({ label, value, isLink }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={fieldLabelStyle}>{label}</div>
    <div style={{
      ...fieldValueStyle,
      color: isLink ? COLORS.brand : COLORS.textPrimary,
      cursor: isLink ? 'pointer' : 'default',
    }}>
      {value}
    </div>
  </div>
);

const TimelineItem = ({ icon, iconColor, iconBg, title, description, timestamp, isLast }) => (
  <div style={{ display: 'flex', gap: '12px', position: 'relative' }}>
    {!isLast && (
      <div style={{ position: 'absolute', left: '15px', top: '32px', bottom: '-12px', width: '2px', backgroundColor: '#dddbda' }} />
    )}
    <div style={{
      width: '32px', height: '32px', borderRadius: '50%', backgroundColor: iconBg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1,
    }}>
      <Icon category="utility" name={icon} size="x-small" style={{ fill: iconColor }} />
    </div>
    <div style={{ flex: 1, paddingBottom: isLast ? '0' : '20px' }}>
      <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.textPrimary, marginBottom: '4px' }}>{title}</div>
      <div style={{ fontSize: '13px', color: '#3e3e3c', marginBottom: '4px' }}>{description}</div>
      <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>{timestamp}</div>
    </div>
  </div>
);

const QuickActionButton = ({ icon, label }) => (
  <button style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
    padding: '12px 8px', backgroundColor: 'transparent', border: `1px solid ${COLORS.border}`,
    borderRadius: '4px', cursor: 'pointer', width: '100%',
  }}>
    <Icon category="utility" name={icon} size="small" style={{ fill: COLORS.brand }} />
    <span style={{ fontSize: '11px', color: '#3e3e3c', fontWeight: '500', textAlign: 'center', lineHeight: '1.3' }}>{label}</span>
  </button>
);

// ---------------------------------------------------------------------------
// Custom DataTable Cells
// ---------------------------------------------------------------------------
const MemberNameCell = ({ children, ...props }) => {
  const item = props.item;
  const initials = item ? item.name.split(' ').map(n => n[0]).join('') : '';
  return (
    <DataTableCell {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{
          width: '28px', height: '28px', borderRadius: '50%', backgroundColor: COLORS.brand,
          color: COLORS.white, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '11px', fontWeight: '700', flexShrink: 0,
        }}>
          {initials}
        </div>
        <span style={{ color: COLORS.brand, fontWeight: '500', cursor: 'pointer' }}>{item.name}</span>
      </div>
    </DataTableCell>
  );
};
MemberNameCell.displayName = DataTableCell.displayName;

const MemberStatusCell = ({ children, ...props }) => {
  const status = props.item && props.item.status;
  const statusColors = {
    'Donated': { bg: '#e8f5e9', color: '#2e844a', border: '#a8d8b0' },
    'Pledged': { bg: '#eef4ff', color: '#0176d3', border: '#b9d6f7' },
    'Responded': { bg: '#f0e6fb', color: '#9050e9', border: '#d4b8f0' },
    'Sent': { bg: '#fef3e8', color: '#fe9339', border: '#fdd5a0' },
    'No Response': { bg: '#f5f5f5', color: '#706e6b', border: '#c9c7c5' },
  };
  const colorSet = statusColors[status] || { bg: '#f3f3f3', color: '#706e6b', border: '#dddbda' };
  return (
    <DataTableCell {...props}>
      <span style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
        fontWeight: '600', backgroundColor: colorSet.bg, color: colorSet.color, border: `1px solid ${colorSet.border}`,
      }}>
        {status}
      </span>
    </DataTableCell>
  );
};
MemberStatusCell.displayName = DataTableCell.displayName;

const CurrencyCell = ({ children, ...props }) => {
  const amount = props.item && props.item.amount;
  if (!amount) return <DataTableCell {...props}><span style={{ color: COLORS.textSecondary }}>--</span></DataTableCell>;
  return (
    <DataTableCell {...props}>
      <span style={{ fontWeight: '600', fontVariantNumeric: 'tabular-nums' }}>
        ${typeof amount === 'number' ? amount.toLocaleString('en-US') : amount}
      </span>
    </DataTableCell>
  );
};
CurrencyCell.displayName = DataTableCell.displayName;

const NameLinkCell = ({ children, ...props }) => (
  <DataTableCell {...props}>
    <span style={{ color: COLORS.brand, cursor: 'pointer', fontWeight: '500' }}>{children}</span>
  </DataTableCell>
);
NameLinkCell.displayName = DataTableCell.displayName;

// ---------------------------------------------------------------------------
// Navigation Bar
// ---------------------------------------------------------------------------
const NavBar = ({ activeTab, onTabChange }) => {
  const tabs = ['Record Home', 'Details', 'Related', 'Activity'];
  return (
    <div style={{
      backgroundColor: 'white', border: `1px solid ${COLORS.border}`, borderRadius: '4px',
      marginBottom: '16px', display: 'flex', alignItems: 'center',
    }}>
      {tabs.map((tab) => (
        <button key={tab} onClick={() => onTabChange(tab)} style={{
          padding: '12px 24px', fontSize: '14px',
          fontWeight: activeTab === tab ? '700' : '400',
          color: activeTab === tab ? COLORS.brand : '#3e3e3c',
          backgroundColor: 'transparent', border: 'none',
          borderBottom: activeTab === tab ? `3px solid ${COLORS.brand}` : '3px solid transparent',
          cursor: 'pointer',
        }}>
          {tab}
        </button>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const NPSPCampaign = () => {
  const [activeNav, setActiveNav] = useState('Record Home');
  const [relatedTab, setRelatedTab] = useState(0);
  const [memberFilter, setMemberFilter] = useState('All');
  const [timelineExpanded, setTimelineExpanded] = useState(true);

  // Campaign data
  const raised = 387500;
  const goal = 500000;
  const progress = (raised / goal) * 100;
  const remaining = goal - raised;

  // Weekly donation flow chart
  const weeklyFlowData = useMemo(() => ({
    labels: ['W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7', 'W8', 'W9', 'W10', 'W11', 'W12', 'W13'],
    datasets: [
      {
        label: 'This Year',
        data: [8500, 12200, 15800, 22400, 18900, 25600, 31200, 28700, 35400, 42100, 38900, 52800, 55000],
        borderColor: SLDS_CHART_COLORS[0],
        backgroundColor: SLDS_CHART_COLORS_ALPHA[0],
        fill: true, tension: 0.35, pointRadius: 3,
        pointBackgroundColor: SLDS_CHART_COLORS[0], borderWidth: 2.5,
      },
      {
        label: 'Last Year',
        data: [6200, 9800, 11500, 18200, 14700, 19800, 24500, 21300, 28700, 32400, 29100, 41200, 43500],
        borderColor: '#939393', backgroundColor: 'transparent',
        borderDash: [6, 4], tension: 0.35, pointRadius: 3,
        pointBackgroundColor: '#939393', borderWidth: 2, fill: false,
      },
    ],
  }), []);

  const weeklyFlowOptions = useMemo(() => ({
    plugins: {
      legend: { position: 'top', align: 'end' },
      tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: $${ctx.parsed.y.toLocaleString()}` } },
    },
    scales: { y: { ticks: { callback: (v) => `$${(v / 1000).toFixed(0)}K` } } },
  }), []);

  // Gift level breakdown doughnut
  const giftLevelData = useMemo(() => ({
    labels: ['Major ($10K+)', 'Mid-Level ($1K-$10K)', 'Annual ($250-$1K)', 'General (<$250)'],
    datasets: [{
      data: [142500, 118750, 78250, 48000],
      backgroundColor: [SLDS_CHART_COLORS[0], SLDS_CHART_COLORS[1], SLDS_CHART_COLORS[2], SLDS_CHART_COLORS[4]],
      borderColor: COLORS.white, borderWidth: 3,
    }],
  }), []);

  const giftLevelOptions = useMemo(() => ({
    cutout: '55%',
    plugins: {
      legend: { position: 'bottom', labels: { font: { size: 11 }, padding: 12, boxWidth: 12 } },
    },
  }), []);

  // Donation type bar chart for sidebar
  const donationTypeData = useMemo(() => ({
    labels: ['Cash', 'Credit Card', 'Stock', 'DAF', 'In-Kind'],
    datasets: [{
      label: 'Amount',
      data: [142000, 98500, 72000, 48000, 27000],
      backgroundColor: SLDS_CHART_COLORS_ALPHA[0],
      borderColor: SLDS_CHART_COLORS[0],
      borderWidth: 1.5, borderRadius: 3, barPercentage: 0.7,
    }],
  }), []);

  const donationTypeOptions = useMemo(() => ({
    indexAxis: 'y',
    plugins: { legend: { display: false } },
    scales: { x: { ticks: { callback: (v) => `$${(v / 1000).toFixed(0)}K` } } },
  }), []);

  // Response rates
  const responseRates = useMemo(() => [
    { channel: 'Email', sent: 1850, responded: 412, donated: 156, rate: 22.3 },
    { channel: 'Direct Mail', sent: 620, responded: 142, donated: 68, rate: 22.9 },
    { channel: 'Phone', sent: 185, responded: 98, donated: 21, rate: 53.0 },
  ], []);

  // Campaign hierarchy
  const campaignHierarchy = useMemo(() => [
    { id: 'h-1', name: 'Annual Fund FY2025', relationship: 'Parent', raised: 1250000, goal: 1500000, status: 'Active' },
    { id: 'h-2', name: 'Year-End Appeal 2025', relationship: 'Current', raised: 387500, goal: 500000, status: 'In Progress' },
    { id: 'h-3', name: 'Spring Gala 2025', relationship: 'Sibling', raised: 425000, goal: 400000, status: 'Completed' },
    { id: 'h-4', name: 'Monthly Giving Drive', relationship: 'Sibling', raised: 187500, goal: 250000, status: 'Active' },
    { id: 'h-5', name: 'Giving Tuesday 2025', relationship: 'Sibling', raised: 98000, goal: 100000, status: 'Completed' },
    { id: 'h-6', name: 'Corporate Partners', relationship: 'Sibling', raised: 152000, goal: 250000, status: 'Active' },
  ], []);

  // Campaign members data
  const allMembers = useMemo(() => [
    { id: 'm-1', name: 'Margaret Morrison', status: 'Donated', amount: 25000, date: '2025-12-28', type: 'Major Donor' },
    { id: 'm-2', name: 'Robert Chen', status: 'Donated', amount: 15000, date: '2025-12-22', type: 'Major Donor' },
    { id: 'm-3', name: 'Sarah Williams', status: 'Donated', amount: 10000, date: '2025-12-20', type: 'Mid-Level' },
    { id: 'm-4', name: 'James Patterson', status: 'Pledged', amount: 7500, date: '2025-12-18', type: 'Mid-Level' },
    { id: 'm-5', name: 'Elena Rodriguez', status: 'Donated', amount: 5000, date: '2025-12-15', type: 'Mid-Level' },
    { id: 'm-6', name: 'David Kim', status: 'Responded', amount: null, date: '2025-12-12', type: 'Prospect' },
    { id: 'm-7', name: 'Jennifer Brooks', status: 'Donated', amount: 2500, date: '2025-12-10', type: 'Annual' },
    { id: 'm-8', name: 'Michael Torres', status: 'Sent', amount: null, date: '2025-12-08', type: 'Prospect' },
    { id: 'm-9', name: 'Lisa Anderson', status: 'Donated', amount: 1000, date: '2025-12-05', type: 'Annual' },
    { id: 'm-10', name: 'Thomas Wright', status: 'No Response', amount: null, date: '2025-12-01', type: 'Lapsed' },
  ], []);

  const filteredMembers = useMemo(() => {
    if (memberFilter === 'All') return allMembers;
    return allMembers.filter(m => m.status === memberFilter);
  }, [allMembers, memberFilter]);

  // Campaign timeline
  const campaignTimeline = useMemo(() => [
    { icon: 'announcement', iconColor: COLORS.brand, iconBg: '#e6f2fb', title: 'Campaign Launched', description: 'Year-End Appeal 2025 officially launched with goal of $500,000. Theme: "Together We Nourish."', timestamp: 'Oct 1, 2025' },
    { icon: 'email', iconColor: COLORS.purple, iconBg: '#f0e6fb', title: 'First Email Blast', description: 'Initial appeal email sent to 1,850 constituents. Open rate: 42%, click rate: 18%.', timestamp: 'Oct 15, 2025' },
    { icon: 'custom_apps', iconColor: COLORS.warning, iconBg: '#fef3e8', title: 'Direct Mail Drop #1', description: 'First wave of 620 personalized appeal letters mailed to mid-level and major donor prospects.', timestamp: 'Nov 1, 2025' },
    { icon: 'check', iconColor: COLORS.success, iconBg: '#e8f5e9', title: '$100K Milestone', description: 'Campaign crossed $100,000 in donations. 78 unique donors contributing. Board match announced.', timestamp: 'Nov 15, 2025' },
    { icon: 'email', iconColor: COLORS.purple, iconBg: '#f0e6fb', title: 'Giving Tuesday Push', description: 'Special Giving Tuesday email and social media campaign. Raised $42,000 in a single day from 89 donors.', timestamp: 'Dec 3, 2025' },
    { icon: 'check', iconColor: COLORS.success, iconBg: '#e8f5e9', title: '$250K Milestone', description: 'Halfway to goal! 167 donors have contributed. Major gift of $25,000 received from Morrison Family.', timestamp: 'Dec 15, 2025' },
    { icon: 'call', iconColor: COLORS.teal, iconBg: '#e6f7f3', title: 'Phone-a-thon Weekend', description: 'Board members and volunteers made 185 personal calls. Secured 21 additional gifts totaling $99,750.', timestamp: 'Dec 20, 2025' },
    { icon: 'trending', iconColor: COLORS.brand, iconBg: '#e6f2fb', title: 'Current: $387,500 Raised', description: '245 donors contributing. $112,500 remaining to reach goal. Final push underway.', timestamp: 'Dec 31, 2025' },
  ], []);

  return (
    <ShowcasePageWrapper
      title="Year-End Appeal 2025"
      description="Fundraising campaign record page"
      icon="campaign"
      iconCategory="standard"
    >
      {/* Record Header */}
      <div style={{ ...cardStyle }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '8px', backgroundColor: '#F49756',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon category="standard" name="campaign" size="medium" style={{ fill: 'white' }} />
            </div>
            <div>
              <div style={{ fontSize: '12px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                Campaign
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h1 style={{ fontSize: '22px', fontWeight: '700', color: COLORS.textPrimary, margin: 0 }}>
                  Year-End Appeal 2025
                </h1>
                <Badge label="In Progress" color={COLORS.white} bgColor={COLORS.success} />
                <Badge label="Fundraising" color={COLORS.white} bgColor={COLORS.purple} />
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <Button label="Edit" variant="neutral" />
            <Button label="Clone" variant="neutral" />
            <Button label="Add Members" variant="neutral" iconCategory="utility" iconName="add" iconPosition="left" />
            <Button label="Send Email" variant="brand" iconCategory="utility" iconName="email" iconPosition="left" />
          </div>
        </div>
      </div>

      {/* Fundraising Progress Bar (compact, spans full width) */}
      <div style={{ ...cardStyle }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: '700', color: COLORS.textPrimary }}>Fundraising Progress</span>
            <span style={{ fontSize: '13px', color: COLORS.textSecondary }}>
              ${raised.toLocaleString()} of ${goal.toLocaleString()} goal
            </span>
          </div>
          <span style={{ fontSize: '14px', fontWeight: '600', color: COLORS.brand }}>
            ${remaining.toLocaleString()} to go
          </span>
        </div>
        <div style={{ position: 'relative' }}>
          <div style={{ height: '24px', backgroundColor: '#e5e5e5', borderRadius: '12px', overflow: 'hidden' }}>
            <div style={{
              width: `${progress}%`, height: '100%',
              background: 'linear-gradient(90deg, #0176d3, #2e844a)',
              borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '10px',
            }}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: COLORS.white }}>{progress.toFixed(1)}%</span>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
            {[25, 50, 75, 100].map(pct => (
              <span key={pct} style={{
                fontSize: '11px',
                color: pct <= progress ? COLORS.textPrimary : COLORS.textSecondary,
                fontWeight: pct <= progress ? '600' : '400',
              }}>
                ${((goal * pct) / 100 / 1000).toFixed(0)}K
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation Bar */}
      <NavBar activeTab={activeNav} onTabChange={setActiveNav} />

      {/* Three-Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '25% 50% 25%', gap: '16px', alignItems: 'start' }}>

        {/* LEFT COLUMN — Highlights */}
        <div>
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '16px', color: COLORS.textPrimary }}>
              Campaign Metrics
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[
                { label: 'Total Raised', value: '$387,500', icon: 'moneybag', color: COLORS.success },
                { label: 'Total Donors', value: '245', icon: 'people', color: COLORS.brand },
                { label: 'Average Gift', value: '$1,582', icon: 'trending', color: COLORS.purple },
                { label: 'Conversion', value: '18.5%', icon: 'check', color: COLORS.teal },
              ].map(m => (
                <div key={m.label} style={{ backgroundColor: COLORS.bg, borderRadius: '4px', padding: '12px', textAlign: 'center' }}>
                  <Icon category="utility" name={m.icon} size="small" style={{ fill: m.color, marginBottom: '6px' }} />
                  <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>{m.label}</div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: COLORS.textPrimary }}>{m.value}</div>
                </div>
              ))}
            </div>

            {/* Goal Progress */}
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${COLORS.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: COLORS.textPrimary }}>Goal Progress</span>
                <span style={{ fontSize: '18px', fontWeight: '700', color: COLORS.success }}>{progress.toFixed(0)}%</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <MiniDonut value={progress} total={100} color={COLORS.success} size={48} />
                <div style={{ flex: 1 }}>
                  {[
                    { label: 'Major Gifts', value: 37, pct: 37 },
                    { label: 'Mid-Level', value: 31, pct: 31 },
                    { label: 'Annual Gifts', value: 20, pct: 20 },
                    { label: 'General', value: 12, pct: 12 },
                  ].map(item => (
                    <div key={item.label} style={{ marginBottom: '4px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '2px' }}>
                        <span style={{ color: COLORS.textSecondary }}>{item.label}</span>
                        <span style={{ fontWeight: '600', color: COLORS.textPrimary }}>{item.value}%</span>
                      </div>
                      <div style={{ height: '3px', backgroundColor: '#e5e5e5', borderRadius: '2px', overflow: 'hidden' }}>
                        <div style={{ width: `${item.pct}%`, height: '100%', backgroundColor: item.pct >= 30 ? COLORS.success : COLORS.brand, borderRadius: '2px' }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Campaign Summary */}
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${COLORS.border}` }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#3e3e3c', marginBottom: '8px' }}>Campaign Summary</div>
              {[
                ['Campaign ROI', '733%'],
                ['Total Cost', '$46,500'],
                ['Cost per Dollar', '$0.12'],
                ['Net Revenue', '$341,000'],
                ['Days Remaining', '0'],
                ['Members', '1,325'],
              ].map(([label, val]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>{label}</span>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: COLORS.textPrimary }}>{val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CENTER COLUMN — Detail + Related */}
        <div>
          {/* Campaign Information */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: COLORS.textPrimary }}>
              Campaign Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
              <FieldDisplay label="Campaign Name" value="Year-End Appeal 2025" />
              <FieldDisplay label="Type" value="Fundraising" />
              <FieldDisplay label="Status" value="In Progress" />
              <FieldDisplay label="Parent Campaign" value="Annual Fund FY2025" isLink />
              <FieldDisplay label="Start Date" value="October 1, 2025" />
              <FieldDisplay label="End Date" value="December 31, 2025" />
              <FieldDisplay label="Expected Revenue" value="$500,000" />
              <FieldDisplay label="Actual Cost" value="$46,500" />
              <FieldDisplay label="Budgeted Cost" value="$52,000" />
              <FieldDisplay label="Num Sent" value="2,655" />
              <FieldDisplay label="Expected Response (%)" value="20%" />
              <FieldDisplay label="Actual Response (%)" value="24.6%" />
              <div style={{ marginBottom: '16px' }}>
                <div style={fieldLabelStyle}>Campaign Owner</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                  <div style={{
                    width: '24px', height: '24px', borderRadius: '50%', backgroundColor: COLORS.brand,
                    color: COLORS.white, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '10px', fontWeight: '700',
                  }}>SK</div>
                  <span style={{ fontSize: '14px', color: COLORS.brand, cursor: 'pointer' }}>Sarah Kim</span>
                </div>
              </div>
              <FieldDisplay label="Created By" value="Sarah Kim" isLink />
              <div style={{ gridColumn: '1 / -1' }}>
                <FieldDisplay label="Description" value="Annual year-end fundraising appeal targeting individual donors. Theme: 'Together We Nourish.' Multi-channel campaign including email, direct mail, and personal calls to raise $500,000 for operating expenses and capital improvements." />
              </div>
            </div>
          </div>

          {/* Related Lists */}
          <div style={cardStyle}>
            <Tabs id="campaign-related-tabs" selectedIndex={relatedTab} onSelect={(index) => setRelatedTab(index)}>
              <TabsPanel label={`Members (${allMembers.length})`}>
                <div style={{ marginTop: '8px' }}>
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    {['All', 'Donated', 'Pledged', 'Responded', 'Sent', 'No Response'].map(filter => (
                      <button key={filter} onClick={() => setMemberFilter(filter)} style={{
                        padding: '4px 10px', fontSize: '12px',
                        fontWeight: memberFilter === filter ? '700' : '400',
                        color: memberFilter === filter ? COLORS.white : COLORS.textPrimary,
                        backgroundColor: memberFilter === filter ? COLORS.brand : COLORS.white,
                        border: `1px solid ${memberFilter === filter ? COLORS.brand : COLORS.border}`,
                        borderRadius: '4px', cursor: 'pointer',
                      }}>
                        {filter}
                      </button>
                    ))}
                  </div>
                  <DataTable items={filteredMembers} id="campaign-members-table" fixedLayout>
                    <DataTableColumn label="Name" property="name" width="200px">
                      <MemberNameCell />
                    </DataTableColumn>
                    <DataTableColumn label="Status" property="status" width="120px">
                      <MemberStatusCell />
                    </DataTableColumn>
                    <DataTableColumn label="Amount" property="amount" width="110px">
                      <CurrencyCell />
                    </DataTableColumn>
                    <DataTableColumn label="Date" property="date" width="110px" />
                    <DataTableColumn label="Type" property="type" width="110px" />
                  </DataTable>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${COLORS.border}` }}>
                    <Button label="View All 245 Members" variant="base" />
                    <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>
                      Total Donated: <strong style={{ color: COLORS.textPrimary }}>
                        ${allMembers.filter(m => m.amount).reduce((sum, m) => sum + m.amount, 0).toLocaleString()}
                      </strong>
                    </span>
                  </div>
                </div>
              </TabsPanel>
              <TabsPanel label={`Hierarchy (${campaignHierarchy.length})`}>
                <div style={{ marginTop: '8px' }}>
                  {campaignHierarchy.map((camp, idx) => {
                    const campProgress = Math.min((camp.raised / camp.goal) * 100, 100);
                    const isCurrent = camp.relationship === 'Current';
                    return (
                      <div key={camp.id} style={{
                        padding: '12px 16px', marginBottom: '8px',
                        backgroundColor: isCurrent ? '#eef4ff' : COLORS.white,
                        borderRadius: '4px',
                        border: isCurrent ? `2px solid ${COLORS.brand}` : `1px solid ${COLORS.border}`,
                        marginLeft: camp.relationship !== 'Parent' ? '24px' : '0',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                              fontSize: '14px', fontWeight: isCurrent ? '700' : '500',
                              color: COLORS.brand, cursor: 'pointer',
                            }}>
                              {camp.name}
                            </span>
                            {isCurrent && <Badge label="Current" color={COLORS.white} bgColor={COLORS.brand} />}
                            {camp.relationship === 'Parent' && <Badge label="Parent" color={COLORS.textSecondary} bgColor={COLORS.bg} />}
                          </div>
                          <span style={{ fontSize: '13px', fontWeight: '600', color: COLORS.textPrimary }}>
                            ${(camp.raised / 1000).toFixed(0)}K / ${(camp.goal / 1000).toFixed(0)}K
                          </span>
                        </div>
                        <div style={{ height: '6px', backgroundColor: '#e5e5e5', borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{
                            width: `${campProgress}%`, height: '100%',
                            backgroundColor: campProgress >= 100 ? COLORS.success : COLORS.brand, borderRadius: '3px',
                          }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                          <span style={{ fontSize: '11px', color: COLORS.textSecondary }}>{campProgress.toFixed(0)}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsPanel>
              <TabsPanel label="Response Rates">
                <div style={{ marginTop: '8px' }}>
                  {responseRates.map((channel, idx) => (
                    <div key={idx} style={{
                      padding: '16px', backgroundColor: idx % 2 === 0 ? COLORS.bg : COLORS.white,
                      borderRadius: '4px', marginBottom: '8px', border: `1px solid ${COLORS.border}`,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontSize: '15px', fontWeight: '600', color: COLORS.textPrimary }}>{channel.channel}</span>
                        <span style={{ fontSize: '14px', fontWeight: '700', color: COLORS.brand }}>{channel.rate}% response</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                        <div>
                          <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', marginBottom: '2px' }}>Sent</div>
                          <div style={{ fontSize: '16px', fontWeight: '700', color: COLORS.textPrimary }}>{channel.sent.toLocaleString()}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', marginBottom: '2px' }}>Responded</div>
                          <div style={{ fontSize: '16px', fontWeight: '700', color: COLORS.textPrimary }}>{channel.responded}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', marginBottom: '2px' }}>Donated</div>
                          <div style={{ fontSize: '16px', fontWeight: '700', color: COLORS.success }}>{channel.donated}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsPanel>
            </Tabs>
          </div>

          {/* Activity Timeline */}
          <div style={cardStyle}>
            <ExpandableSection title="Campaign Timeline" isOpen={timelineExpanded} onToggleOpen={() => setTimelineExpanded(!timelineExpanded)}>
              <div style={{ padding: '8px 0' }}>
                {campaignTimeline.map((a, idx) => (
                  <TimelineItem key={idx} icon={a.icon} iconColor={a.iconColor} iconBg={a.iconBg}
                    title={a.title} description={a.description} timestamp={a.timestamp} isLast={idx === campaignTimeline.length - 1} />
                ))}
              </div>
            </ExpandableSection>
            <div style={{ textAlign: 'center', marginTop: '8px', borderTop: `1px solid ${COLORS.border}`, paddingTop: '12px' }}>
              <Button label="View All Activity" variant="base" />
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN — Sidebar */}
        <div>
          {/* Quick Actions */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: COLORS.textPrimary }}>Quick Actions</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
              <QuickActionButton icon="email" label="Send Email" />
              <QuickActionButton icon="add" label="Add Member" />
              <QuickActionButton icon="call" label="Log a Call" />
              <QuickActionButton icon="task" label="New Task" />
              <QuickActionButton icon="event" label="New Event" />
              <QuickActionButton icon="note" label="New Note" />
            </div>
          </div>

          {/* Weekly Donation Flow */}
          <div style={cardStyle}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#3e3e3c', marginBottom: '8px' }}>
              Weekly Donation Flow
            </div>
            <SLDSLineChart data={weeklyFlowData} options={weeklyFlowOptions} height={160} />
          </div>

          {/* Gift Level Breakdown */}
          <div style={cardStyle}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#3e3e3c', marginBottom: '8px' }}>
              Gift Level Breakdown
            </div>
            <SLDSDoughnutChart data={giftLevelData} options={giftLevelOptions} height={180} />
          </div>

          {/* By Donation Type */}
          <div style={cardStyle}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#3e3e3c', marginBottom: '8px' }}>
              By Donation Type
            </div>
            <SLDSBarChart data={donationTypeData} options={donationTypeOptions} height={160} />
          </div>

          {/* Cost & ROI */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: COLORS.textPrimary }}>Cost & ROI</h3>
            {[
              { label: 'Total Cost', value: '$46,500' },
              { label: 'Cost per Dollar', value: '$0.12' },
              { label: 'Cost per Donor', value: '$189.80' },
              { label: 'Net Revenue', value: '$341,000' },
              { label: 'ROI', value: '733%' },
            ].map((item, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: idx < 4 ? `1px solid ${COLORS.bg}` : 'none' }}>
                <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>{item.label}</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: idx === 4 ? COLORS.success : COLORS.textPrimary }}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ShowcasePageWrapper>
  );
};

export default NPSPCampaign;
